import * as React from "react";

import {
  deviceTypeChoices,
  NO_DEVICE_TYPE,
} from "@/shared/components/devices/deviceTypeChoices";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { type Device } from "@/shared/domain/devices";
import { useSetDevicesType } from "@/shared/domain/devices/hooks/useSetDevicesType";
import { useAllDeviceTypesByOrg } from "@/shared/domain/deviceTypes/hooks";
import {
  Dialog,
  Radio,
  RadioGroup,
  RobotoButton,
} from "@/shared/foundation/primitives";

import {
  collectDeviceFailures,
  DeviceFailureAlert,
} from "../DeviceFailureAlert";

import styles from "./SetDeviceTypeDialog.module.css";

interface SetDeviceTypeDialogProps {
  /** The devices to change; an empty list keeps the dialog closed. */
  devices: readonly Device[];
  onClose: () => void;
  /**
   * Called after each attempt that changed at least one device, with the
   * devices that failed. The failed ones stay in the dialog for a retry; an
   * empty list means every device was changed.
   */
  onChanged: (failed: readonly Device[]) => void;
}

/**
 * Gives every selected device one type, or clears their types. The dialog
 * stays open while the change runs, so a late failure list has somewhere to
 * show.
 */
export function SetDeviceTypeDialog({
  devices,
  onClose,
  onChanged,
}: SetDeviceTypeDialogProps) {
  const open = devices.length > 0;
  const setDevicesType = useSetDevicesType();
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !setDevicesType.isPending) {
          onClose();
        }
      }}
    >
      <Dialog.Content className={styles.dialog}>
        {open && (
          // Mounted per opening, so each opening starts from a clean form.
          <SetDeviceTypeForm
            devices={devices}
            setDevicesType={setDevicesType}
            onClose={onClose}
            onChanged={onChanged}
          />
        )}
      </Dialog.Content>
    </Dialog>
  );
}

function SetDeviceTypeForm({
  devices,
  setDevicesType,
  onClose,
  onChanged,
}: SetDeviceTypeDialogProps & {
  setDevicesType: ReturnType<typeof useSetDevicesType>;
}) {
  const orgId = devices[0].org_id;
  const deviceTypesQuery = useAllDeviceTypesByOrg(orgId);
  const showNotification = useNotification();
  const [isBulk] = React.useState(() => devices.length > 1);
  // Shrinks to the devices that failed, so a retry sends only those.
  const [remaining, setRemaining] = React.useState(devices);
  // The chosen radio: a `dt_` ID, `NO_DEVICE_TYPE` to clear the type, or
  // `null` until the user picks, so a stray click cannot clear every type.
  const [choice, setChoice] = React.useState<string | null>(null);
  const deviceTypeId = choice === NO_DEVICE_TYPE ? null : choice;
  const [failures, setFailures] = React.useState<{
    attempted: number;
    messages: ReadonlyMap<string, string>;
  }>({ attempted: 0, messages: new Map() });

  const count = remaining.length;
  const subject = count === 1 ? "1 device" : `${count} devices`;
  const typeName =
    deviceTypesQuery.data?.find((type) => type.device_type_id === deviceTypeId)
      ?.name ?? deviceTypeId;

  const onConfirm = async () => {
    if (choice === null) {
      return;
    }
    const results = await setDevicesType.mutateAsync({
      devices: remaining,
      deviceTypeId,
    });
    const { failed, messages } = collectDeviceFailures(
      remaining,
      results,
      "Failed to set the device's type. Please try again.",
    );
    const changed = remaining.length - failed.length;
    if (changed > 0) {
      const changedSubject = changed === 1 ? "1 device" : `${changed} devices`;
      showNotification(
        deviceTypeId === null
          ? `Cleared the type of ${changedSubject}`
          : `Set the type of ${changedSubject} to ${typeName}`,
      );
      onChanged(failed);
    }
    setRemaining(failed);
    setFailures({ attempted: remaining.length, messages });
  };

  // Every device is changed, and the caller closes the dialog on `onChanged([])`.
  if (count === 0) {
    return null;
  }

  return (
    <div className={styles.body}>
      <Dialog.Title>Set device type</Dialog.Title>
      <Dialog.Description>
        Choose the type for {subject}, or None to clear it. A device inherits
        its type&apos;s configurations.
      </Dialog.Description>
      <DeviceFailureAlert {...failures} isBulk={isBulk} outcome="changed" />
      {/* Radios instead of a Select, because the Select's popup renders
          beneath the Dialog. */}
      <RadioGroup<string>
        legend="Type"
        value={choice}
        onValueChange={setChoice}
      >
        {deviceTypeChoices(deviceTypesQuery.data).map((option) => (
          <Radio<string>
            key={option.value}
            value={option.value}
            label={option.label}
          />
        ))}
      </RadioGroup>
      <div className={styles.actions}>
        <RobotoButton
          variant="secondary"
          disabled={setDevicesType.isPending}
          onClick={onClose}
        >
          Cancel
        </RobotoButton>
        <RobotoButton
          variant="primary"
          loading={setDevicesType.isPending}
          disabled={choice === null || setDevicesType.isPending}
          onClick={() => void onConfirm()}
        >
          {choice === NO_DEVICE_TYPE ? "Clear type" : "Set type"}
        </RobotoButton>
      </div>
    </div>
  );
}
